import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cx } from '@/lib/cx';
import { Button } from './controls';
import { Icon, type IconName } from './Icon';

// ---------- Modal / Drawer (native <dialog>: focus trap, Escape, inert page, focus return) ----------

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg';
  /** Disallow Escape / backdrop close, e.g. while a command resolves. */
  dismissible?: boolean;
}

function useDialog(open: boolean, onClose: () => void, dismissible: boolean) {
  const ref = useRef<HTMLDialogElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      returnTo.current = document.activeElement as HTMLElement | null;
      el.showModal();
    } else if (!open && el.open) {
      el.close();
      returnTo.current?.focus?.();
    }
  }, [open]);
  // Unmounted while open (e.g. a URL-driven drawer whose search param went away, or Back): close
  // and give focus back all the same.
  useEffect(() => {
    const el = ref.current;
    return () => {
      if (!el?.open) return;
      el.close();
      if (returnTo.current?.isConnected) returnTo.current.focus();
    };
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onCancel = (e: Event) => {
      e.preventDefault();
      if (dismissible) onClose();
    };
    el.addEventListener('cancel', onCancel);
    return () => el.removeEventListener('cancel', onCancel);
  }, [dismissible, onClose]);
  const onBackdrop = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (dismissible && e.target === e.currentTarget) onClose();
  };
  return { ref, onBackdrop };
}

export function Modal({ open, onClose, title, description, children, footer, size = 'md', dismissible = true }: DialogProps) {
  const { ref, onBackdrop } = useDialog(open, onClose, dismissible);
  const titleId = useId();
  const descId = useId();
  return (
    <dialog
      ref={ref}
      className={cx('modal', size === 'lg' && 'modal-lg')}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      onClick={onBackdrop}
    >
      {open ? (
        <>
          <header className="modal-header">
            <div>
              <h2 id={titleId} style={{ fontSize: 'var(--text-lg)' }}>
                {title}
              </h2>
              {description ? (
                <p id={descId} className="small muted" style={{ marginTop: 2 }}>
                  {description}
                </p>
              ) : null}
            </div>
            {dismissible ? <Button variant="quiet" iconOnly icon="x" aria-label="Close" onClick={onClose} /> : null}
          </header>
          <div className="modal-body">{children}</div>
          {footer ? <footer className="modal-footer">{footer}</footer> : null}
        </>
      ) : null}
    </dialog>
  );
}

export function Drawer({ open, onClose, title, description, children, footer, dismissible = true }: DialogProps) {
  const { ref, onBackdrop } = useDialog(open, onClose, dismissible);
  const titleId = useId();
  return (
    <dialog ref={ref} className="drawer" aria-labelledby={titleId} onClick={onBackdrop}>
      {open ? (
        <>
          <header className="drawer-header">
            <div>
              <h2 id={titleId} style={{ fontSize: 'var(--text-lg)' }}>
                {title}
              </h2>
              {description ? <p className="small muted">{description}</p> : null}
            </div>
            {dismissible ? <Button variant="quiet" iconOnly icon="x" aria-label="Close" onClick={onClose} /> : null}
          </header>
          <div className="drawer-body">{children}</div>
          {footer ? <footer className="drawer-footer">{footer}</footer> : null}
        </>
      ) : null}
    </dialog>
  );
}

// ---------- Menu (menu button pattern) ----------

export interface MenuItem {
  label: string;
  icon?: IconName;
  onSelect?: () => void;
  disabled?: boolean;
  /** Shown as the reason when disabled. */
  hint?: string;
  danger?: boolean;
  /** Marks the current choice (e.g. the active organization). */
  checked?: boolean;
}

export function Menu({
  label,
  items,
  icon = 'more',
  iconOnly = true,
  align = 'right',
  size = 'sm',
  trigger,
}: {
  label: string;
  items: Array<MenuItem | 'separator' | { heading: string }>;
  icon?: IconName;
  iconOnly?: boolean;
  align?: 'left' | 'right';
  size?: 'sm' | 'md';
  /** Custom trigger content (e.g. an avatar); `label` stays the accessible name. */
  trigger?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  // Fixed positioning so scroll containers (tables, cards) never clip the menu; opens upward near the viewport bottom.
  useLayoutEffect(() => {
    if (!open) return;
    const b = buttonRef.current?.getBoundingClientRect();
    const h = listRef.current?.offsetHeight ?? 0;
    if (!b) return;
    const below = b.bottom + 4 + h <= window.innerHeight;
    setPos({
      position: 'fixed',
      top: below ? b.bottom + 4 : Math.max(4, b.top - 4 - h),
      ...(align === 'right' ? { right: window.innerWidth - b.right } : { left: b.left }),
    });
    const close = () => setOpen(false);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const first = listRef.current?.querySelector<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)');
    first?.focus({ preventScroll: true });
    const onDoc = (e: MouseEvent) => {
      if (!listRef.current?.contains(e.target as Node) && !buttonRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const els = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)') ?? []);
    const idx = els.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      els[(idx + 1) % els.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      els[(idx - 1 + els.length) % els.length]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      els[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      els[els.length - 1]?.focus();
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      setOpen(false);
      if (e.key === 'Escape') buttonRef.current?.focus();
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {trigger ? (
        <button
          ref={buttonRef}
          type="button"
          className="menu-trigger"
          aria-label={label}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          onClick={() => setOpen((o) => !o)}
        >
          {trigger}
        </button>
      ) : (
        <Button
          ref={buttonRef}
          size={size}
          variant="quiet"
          icon={icon}
          iconOnly={iconOnly}
          aria-label={iconOnly ? label : undefined}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          onClick={() => setOpen((o) => !o)}
        >
          {iconOnly ? null : label}
        </Button>
      )}
      {open ? (
        <div
          ref={listRef}
          id={menuId}
          role="menu"
          aria-label={label}
          className="popover"
          style={pos}
          onKeyDown={onKeyDown}
        >
          {items.map((item, i) =>
            item === 'separator' ? (
              <div key={`sep-${i}`} className="menu-sep" role="separator" />
            ) : 'heading' in item ? (
              <div key={`h-${i}`} className="menu-heading" role="presentation">
                {item.heading}
              </div>
            ) : (
              <button
                key={item.label}
                type="button"
                role={item.checked === undefined ? 'menuitem' : 'menuitemradio'}
                aria-checked={item.checked}
                className={cx('menu-item', item.danger && 'menu-item-danger')}
                disabled={item.disabled}
                title={item.disabled ? item.hint : undefined}
                onClick={() => {
                  setOpen(false);
                  buttonRef.current?.focus();
                  item.onSelect?.();
                }}
              >
                {item.icon ? <Icon name={item.icon} /> : null}
                <span className="grow">{item.label}</span>
                {item.checked ? <Icon name="check" /> : null}
                {item.disabled && item.hint ? <span className="xsmall faint">{item.hint}</span> : null}
              </button>
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}

// ---------- Tabs (in-page panels, automatic activation, arrow keys) ----------

export interface TabDef {
  id: string;
  label: ReactNode;
  badge?: ReactNode;
}

export function Tabs({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: TabDef[];
  value: string;
  onChange: (id: string) => void;
  label: string;
}) {
  const baseId = useId();
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const idx = tabs.findIndex((t) => t.id === value);
    let next = -1;
    if (e.key === 'ArrowRight') next = (idx + 1) % tabs.length;
    if (e.key === 'ArrowLeft') next = (idx - 1 + tabs.length) % tabs.length;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = tabs.length - 1;
    if (next >= 0) {
      e.preventDefault();
      const t = tabs[next];
      if (t) {
        onChange(t.id);
        document.getElementById(`${baseId}-tab-${t.id}`)?.focus();
      }
    }
  };
  return (
    <div role="tablist" aria-label={label} className="tabs" onKeyDown={onKeyDown}>
      {tabs.map((t) => (
        <button
          key={t.id}
          id={`${baseId}-tab-${t.id}`}
          type="button"
          role="tab"
          className="tab"
          aria-selected={t.id === value}
          tabIndex={t.id === value ? 0 : -1}
          onClick={() => onChange(t.id)}
        >
          {t.label}
          {t.badge}
        </button>
      ))}
    </div>
  );
}

// ---------- Toasts ----------

/** neutral: confirmation. accent: something failed or needs a decision (announced as an alert). */
export type ToastTone = 'neutral' | 'accent';
interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  body?: string;
  action?: ToastAction;
}

/** One optional inline action, e.g. Undo. Running it dismisses the toast. */
export interface ToastAction {
  label: string;
  onAction: () => void;
}

interface ToastApi {
  show: (t: { tone?: ToastTone; title: string; body?: string; durationMs?: number; action?: ToastAction }) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const show = useCallback<ToastApi['show']>(
    ({ tone = 'neutral', title, body, durationMs = 6000, action }) => {
      const id = ++seq.current;
      setItems((xs) => [...xs.slice(-3), { id, tone, title, body, action }]);
      window.setTimeout(() => dismiss(id), durationMs);
    },
    [dismiss],
  );
  const value = useMemo(() => ({ show }), [show]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" role="region" aria-label="Notifications" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={cx('toast', t.tone === 'accent' && 'toast-accent')} role={t.tone === 'accent' ? 'alert' : 'status'}>
            <div className="grow">
              <div className="toast-title">{t.title}</div>
              {t.body ? <div className="muted">{t.body}</div> : null}
            </div>
            {t.action ? (
              <Button
                size="sm"
                variant="quiet"
                className="toast-action"
                onClick={() => {
                  dismiss(t.id);
                  t.action?.onAction();
                }}
              >
                {t.action.label}
              </Button>
            ) : null}
            <Button size="sm" variant="quiet" iconOnly icon="x" aria-label="Dismiss" onClick={() => dismiss(t.id)} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast outside ToastProvider');
  return ctx;
}
